import ENV from 'react-native-config';

import {
    ErrorEvent,
    Event,
    EventHint, // @ts-ignore
} from '@sentry/types/build/types-ts3.8/event';
import Axios, { AxiosError, AxiosResponse } from 'axios';

import debugLog from 'utils/debugLog.ts';

export const createInstance = () =>
    Axios.create({
        baseURL: ENV.BACKEND_BASE_URL,
        headers: {
            'Content-Type': 'application/json',
        },
    });

export function extractRoot(endpoint: string): string | null {
    const match = endpoint.match(/^\/([a-zA-Z]+)\//);
    if (match) {
        return match[1];
    }

    return null;
}

export function getSettledResponseData<T = AxiosResponse<any>>(
    // eslint-disable-next-line no-undef
    settledResponse: PromiseSettledResult<T>,
) {
    if (settledResponse.status === 'fulfilled') {
        return settledResponse.value;
    }

    return undefined;
}

export function onSentryBeforeSend(
    event: ErrorEvent,
    hint: EventHint,
): Event | null {
    const maybeAxiosError = hint.originalException as AxiosError;

    if (maybeAxiosError?.isAxiosError) {
        if (maybeAxiosError.response?.data) {
            if (
                maybeAxiosError.response.status === 401 ||
                maybeAxiosError.response.status === 403
            ) {
                return null;
            }

            return {
                ...event,
                contexts: {
                    ...event.contexts,
                    errorResponse: {
                        status: maybeAxiosError.response.status,
                        url: maybeAxiosError.response.config.url,
                        data: maybeAxiosError.response.data,
                    },
                },
            };
        }
    }

    return event;
}

/**
 * Promise-based delay function for retry mechanism
 * @param attempt Current retry attempt number
 * @param retryCount Maximum number of retry attempts
 * @param retryDuration Total duration over which retries should be spread
 * @returns Promise that resolves after the calculated delay
 */
export function delayRetry(
    attempt: number,
    retryCount: number,
    retryDuration: number,
): Promise<void> {
    if (attempt > 0) {
        const baseInterval = retryDuration / retryCount;
        const jitteredDelay = Math.floor(Math.random() * baseInterval);

        debugLog(
            'delayRetry',
            `delay of ${jitteredDelay}ms before ${attempt} retry attempt`,
        );

        return new Promise((resolve) => setTimeout(resolve, jitteredDelay));
    }

    return Promise.resolve();
}

export class RetryAbortController {
    abortController: AbortController;

    aborted: boolean;

    constructor() {
        this.abortController = new AbortController();
        this.aborted = false;
    }

    public abort() {
        if (!this.aborted) {
            this.aborted = true;
            this.abortController.abort();
        }
    }

    get signal() {
        return this.abortController.signal;
    }
}
